var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var BinningConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
export var BinningMethod;
(function (BinningMethod) {
    BinningMethod["Interval"] = "interval";
    BinningMethod["AutoInterval"] = "auto_interval";
    BinningMethod["Quantile"] = "quantile";
    BinningMethod["ManualQuantile"] = "manual_quantile";
})(BinningMethod || (BinningMethod = {}));
const schema = {
    $id: 'urn:wisepipe-common-pipe:BinningConfig',
    type: 'object',
    properties: {
        method: { type: 'string', enum: Object.values(BinningMethod) },
        numBins: { type: 'integer', minimum: 1 },
        srcColumn: { type: 'string', minLength: 1 },
        columnName: { type: 'string', minLength: 1 },
        groupByColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        }
    },
    required: ['numBins', 'srcColumn', 'columnName'],
    if: { properties: { method: { const: BinningMethod.Interval } } },
    then: {
        properties: {
            bins: {
                type: 'array',
                items: { type: 'number' },
                uniqueItems: true,
                minItems: { $data: '1/numBins' },
                maxItems: { $data: '1/numBins' }
            }
        },
        required: ['bins']
    },
    else: {
        if: { properties: { method: { const: BinningMethod.ManualQuantile } } },
        then: {
            properties: {
                bins: {
                    type: 'array',
                    items: {
                        type: 'number',
                        exclusiveMinimum: 0,
                        exclusiveMaximum: 100
                    },
                    uniqueItems: true
                }
            },
            required: ['bins']
        }
    }
};
let BinningConfig = BinningConfig_1 = class BinningConfig extends ToolConfig {
    constructor(method, numBins, srcColumn, columnName, bins, groupByColumns, customFormats) {
        super(ToolType.BINNING, customFormats);
        this.method = method;
        this.numBins = numBins;
        this.srcColumn = srcColumn;
        this.columnName = columnName;
        if (bins !== undefined)
            this.bins = bins;
        if (groupByColumns !== undefined)
            this.groupByColumns = groupByColumns;
        JSON.validate(this, BinningConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return BinningConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return BinningConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return BinningConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return BinningConfig_1.outs;
    }
    static get in() {
        return BinningConfig_1.ins[0];
    }
    static get out() {
        return BinningConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.BINNING.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.ORGANIZE], this.ins, this.outs, ['cut', 'group'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            // groupby for outlier only is used in the calculation, its not used to "restrict" the which columns show up.  so even if you add
            // groupby columns, non-grpby cols still show up.
            const dependents = this.copyAllInsWithGroupby(exp, source, this.in, this.srcColumn, this.groupByColumns);
            exp
                .makeNew(new Column(this.columnName, 'string'), dependents, true)
                .makeNew(new Column(`${this.columnName} Midpoint`, 'number'), dependents, true);
            const newColumnFormats = new Map();
            const srcColumnMetaData = exp.findSourceColumn(this.in, this.srcColumn);
            if (srcColumnMetaData?.customFormat) {
                newColumnFormats.set(`${this.columnName} Midpoint`, srcColumnMetaData.customFormat.copyWithAutoGeneratedTrue());
            }
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const inputs = new Map([[this.srcColumn, ['number']]]);
        this.addGroupByToInputs(inputs, this.groupByColumns);
        const outputs = [this.columnName, `${this.columnName} Midpoint`];
        const sourceValidationResult = validateSourceWithDetail(source, inputs, outputs);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
BinningConfig = BinningConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.BINNING)
], BinningConfig);
export { BinningConfig };
//# sourceMappingURL=binningConfig.js.map